import { Component, OnDestroy, OnInit } from '@angular/core';
import { CustomStoreFinderService } from './core/custom-store-finder.service';
import { combineLatest, Subscription, switchMap, take } from 'rxjs';
import { CmsComponentData } from '@spartacus/storefront';
import { Option } from '../../../../core/models/option.model';
import { ToolsService } from '../../../../core/services/tools.service';
import { LoaderService } from '../../../../components/loader/loader.service';
import { ToasterService } from '../../../../components/toaster/toaster.service';
import { TOASTER_TYPE } from '../../../../components/toaster/toaster.model';
import { CmsService, TranslationService } from '@spartacus/core';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'custom-store-finder',
  templateUrl: './custom-store-finder.component.html',
  styleUrl: './custom-store-finder.component.scss',
  standalone: false,
})
export class CustomStoreFinderComponent implements OnInit, OnDestroy {
  subscription = new Subscription();
  subscriptionFormValues = new Subscription();

  constructor(
    public componentData: CmsComponentData<any>,
    private customStoreFinderService: CustomStoreFinderService,
    private toolsService: ToolsService,
    private loaderService: LoaderService,
    private toasterService: ToasterService,
    private translationService: TranslationService,
    private cmsService: CmsService,
  ) {}

  ngOnInit(): void {
    setTimeout(() => {
      this.loaderService.init();
    });

    this.loadMaps();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.customStoreFinderService.clearStores();
  }

  loadMaps() {
    this.toolsService.loadExternalScript(
      'https://maps.googleapis.com/maps/api/js?key=' + environment.googleMapsAPIKey,
      () => {
        this.populateComponent();
      },
      () => {
        this.translationService
          .translate('generic.error')
          .pipe(take(1))
          .subscribe(textError => {
            this.toasterService.init({
              type: TOASTER_TYPE.DANGER,
              content: textError,
            });
          });
      },
    );
  }

  populateComponent() {
    this.subscriptionFormValues.add(
      this.componentData.data$
        .pipe(
          switchMap(data => {
            this.customStoreFinderService.googleMapIsLoaded$.next(true);

            const distancesID = (data.distanceOptions || '').split(' ');
            const getsDistances = distancesID.map((id: string) => this.cmsService.getComponentData(id));

            const unitsID = (data.unitOptions || '').split(' ');
            const getsUnits = unitsID.map((id: string) => this.cmsService.getComponentData(id));

            const productsID = (data.productOptions || '').split(' ');
            const getsProducts = productsID.map((id: string) => this.cmsService.getComponentData(id));

            const typesID = (data.typeOptions || '').split(' ');
            const getsTypes = typesID.map((id: string) => this.cmsService.getComponentData(id));

            return combineLatest([
              combineLatest(getsDistances),
              combineLatest(getsUnits),
              combineLatest(getsProducts),
              combineLatest(getsTypes),
            ]);
          }),
        )
        .subscribe(([distances, units, products, types]: any) => {
          const convertToOptions = (data: any): Option[] => {
            return (data || []).map((item: any) => ({
              code: item.code,
              label: item.label,
              selected: item.selected === 'true',
            }));
          };

          const allOptions = (options: Option[]): Option => {
            const allOptions: string[] = [];

            options.forEach(option => {
              allOptions.push(option.code);
            });

            return {
              code: allOptions.join(','),
              label: 'ALL',
            };
          };

          this.setDistances(convertToOptions(distances));
          this.setUnits(convertToOptions(units));
          this.setProducts([...convertToOptions(products), allOptions(convertToOptions(products))]);
          this.setTypes(convertToOptions(types));

          this.loaderService.init({ status: false });

          if (distances.length && units.length && products.length && types.length) {
            this.subscriptionFormValues.unsubscribe();
          }
        }),
    );
  }

  setDistances(options: Option[]) {
    this.customStoreFinderService.distances$.next(options);
  }

  setUnits(options: Option[]) {
    this.customStoreFinderService.units$.next(options);
  }

  setProducts(options: Option[]) {
    this.customStoreFinderService.products$.next(options);
  }

  setTypes(options: Option[]) {
    this.customStoreFinderService.types$.next(options);
  }
}
